import { Component, computed, inject, input, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { NavigationExtras, Router, RouterModule } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ActiveAssignment } from '../../../api/app-types/assignment';
import { DashboardItem } from '../../../api/app-types/dashboard.app';
import { TYPE_OF_CARE, typeOfCareToShort } from '../../../api/app-types/type-of-care';
import { LvrPermissionDirective } from '../../../shared/permissions/permission.directive';

export interface AssignmentViewQueryParams {
	displayName: string;
	openMain?: number;
	openChild?: number;
}
@Component({
	selector: 'lvr-client-card',
	imports: [RouterModule, MatCardModule, TranslocoPipe, MatIcon, LvrPermissionDirective],
	templateUrl: './client-card.html',
	styleUrl: './client-card.scss',
})
export class ClientCard {
	public readonly client = input.required<DashboardItem>();
	private readonly router = inject(Router);
	private readonly selectedAssignmentId = signal<number | null>(null);
	public readonly assignmentsExpanded = signal(false);
	public readonly activeNavigation = signal<string | null>(null);

	public readonly isOpeningAssignmentOverview = computed(
		() => this.activeNavigation() === `assignment-overview:${this.client().id}`,
	);

	public isOpeningAssignment(assignmentId: number): boolean {
		return this.activeNavigation() === `assignment:${assignmentId}`;
	}

	private isMain(a: ActiveAssignment) {
		return !a.isAdditionalAssignment && !a.isServiceOfficeStatement;
	}

	public readonly assignmentsSorted = computed<ActiveAssignment[]>(() => {
		const list = this.client().activeAssignments ?? [];
		if (!list.length) return [];

		const mainIndex = list.findIndex((a) => this.isMain(a));
		if (mainIndex <= 0) return [...list];

		const copy = [...list];
		const [main] = copy.splice(mainIndex, 1);
		return [main, ...copy];
	});

	public readonly mainAssignment = computed<ActiveAssignment | null>(() => {
		const list = this.assignmentsSorted();
		return list.length ? list[0] : null;
	});

	public readonly selectedAssignment = computed<ActiveAssignment | null>(() => {
		const list = this.assignmentsSorted();
		const selectedId = this.selectedAssignmentId();

		if (!list.length) return null;
		if (selectedId == null) return this.mainAssignment();

		return list.find((x) => x.activeAssignmentId === selectedId) ?? this.mainAssignment();
	});

	public readonly overlayAssignments = computed(() => {
		const list = this.assignmentsSorted();
		return list.length > 1 ? list.slice(1) : [];
	});

	public async onCardSelected(e: Event) {
		if (this.activeNavigation()) return;

		const el = e.target as HTMLElement;
		if (el.closest('.assignments-panel, .assignments-overlay')) {
			return;
		}

		await this.navigateWithLoading(`client:${this.client().id}`, ['/desk/client', this.client().id.toString()]);
	}

	public toggleAssignments(event: Event) {
		event.preventDefault();
		event.stopPropagation();

		if (this.assignmentsSorted().length <= 1) return;

		this.assignmentsExpanded.update((v) => !v);
	}

	public async openAssignment(event: Event, assignmentId: number) {
		event.stopPropagation();
		const list = this.assignmentsSorted();
		const selected = list.find((a) => a.activeAssignmentId === assignmentId);

		const queryParams: AssignmentViewQueryParams = {
			displayName: this.client().fullName,
		};

		if (selected?.isAdditionalAssignment) {
			const mainId = this.mainAssignment()?.activeAssignmentId;

			if (mainId) {
				queryParams.openMain = mainId;
				queryParams.openChild = assignmentId;
			}
		} else {
			queryParams.openMain = assignmentId;
		}

		await this.navigateWithLoading(
			`assignment:${assignmentId}`,
			['/desk/client', this.client().id.toString(), 'assignments'],
			{ queryParams },
		);
	}

	public async goToAssignmentOverview(event: Event, clientId: number) {
		event.stopPropagation();

		await this.navigateWithLoading(`assignment-overview:${clientId}`, [
			'/desk/client',
			clientId.toString(),
			'assignments',
		]);
	}

	public phaseButtonLabel(a: ActiveAssignment): string {
		if (!a.phase?.remark) return 'client.client_card.phase.unknown';
		return `client.client_card.phase.${(a.phase?.remark ?? '').toLowerCase()}`;
	}

	public typeOfCareLabel(a: ActiveAssignment) {
		if (a.phase?.remark === 'P1' || a.phase?.remark === 'P2' || !a.typeOfCare) return '';
		return typeOfCareToShort[a.typeOfCare as TYPE_OF_CARE] ?? '';
	}

	public typeOfAssignmentLabel(a: ActiveAssignment) {
		if (a.isAdditionalAssignment) return 'client.client_card.assignment.additional';
		if (a.isServiceOfficeStatement) return 'client.client_card.assignment.fds';
		return '';
	}

	private async navigateWithLoading(key: string, commands: string[], extras?: NavigationExtras): Promise<void> {
		if (this.activeNavigation()) return;

		this.activeNavigation.set(key);
		try {
			await this.router.navigate(commands, extras);
		} finally {
			this.activeNavigation.set(null);
		}
	}
}
